<!--
  Minimales Live-Ticker-Widget: zeigt NUR Ergebnis + Zeit/Status für EIN Spiel (per match-id),
  bewusst OHNE Teamnamen - für Einbettungen, wo nur der reine Spielstand gebraucht wird (z.B.
  eine kleine Ticker-Anzeige an anderer Stelle auf der Website, wo die Teams schon anderweitig
  klar sind).

  Custom Element: <tm-liveticker match-id="12345"></tm-liveticker>
-->
<template>
  <!-- GEÄNDERT: Widget zeigt sich NUR, wenn das Spiel tatsächlich live ist - sonst komplett
       unsichtbar (kein Spinner, keine Fehlermeldung), damit es sich auf der Host-Seite lautlos
       ein-/ausblendet, statt dauerhaft einen Platzhalter zu belegen. -->
  <div v-if="status === 'live'" class="tm-liveticker">
    <div class="tm-liveticker__inhalt">

      <div class="tm-liveticker__inhalt_1">
        <span class="tm-liveticker__ergebnis">
          {{ match.heimtore ?? '-' }}:{{ match.austore ?? '-' }}
        </span>
      </div>
      <div class="tm-liveticker__inhalt_2">
        <span class="tm-liveticker__live-dot" aria-label="Live"></span>
        <span class="tm-liveticker__status">
          Live{{ spieluhrText ? ' ' + spieluhrText : '' }}
        </span>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useClock } from './composables/useClock.js'
import { useLiveScores } from './composables/useLiveScores.js'
import { ermittleAnzeigeStatus, ermittleWsUrl } from './lib/format.js'
import { ladeEinzelnesMatch } from './lib/matchLookup.js'

const props = defineProps({
  matchId: { type: [String, Number], required: true },
  apiBase: { type: String, default: '/rest/courseservice/oddsservice/free' },
  wsUrl: { type: String, default: '/ws-result-service' },
  // Ab wie vielen Minuten vor Anpfiff der Countdown clientseitig gezeigt wird - identisch zu
  // den anderen Widgets.
  countdownAbMinuten: { type: Number, default: 120 }
})

const match = ref(null)
const loading = ref(true)
const error = ref(null)
const { jetzt } = useClock()
const emit = defineEmits(['live-update', 'match-loaded'])

// useLiveScores erwartet ein reaktives Array (so wie SpielplanWidget/StartseiteWidget es mit
// ihrer vollen Match-Liste tun) - hier nur EIN Match, daher als Ein-Element-Array gewrappt,
// damit dieselbe Composable unverändert wiederverwendet werden kann statt eine eigene,
// abgespeckte Variante nur für diesen Fall zu bauen.
const matchesWrapper = computed(() => match.value ? [match.value] : [])
const { verbinden, aufraeumen: wsAufraeumen, wiederholeGepuffertes } = useLiveScores(ermittleWsUrl(props.wsUrl), matchesWrapper)

const status = computed(() => match.value ? ermittleAnzeigeStatus(match.value, jetzt.value, props.countdownAbMinuten) : null)

// "HT" bei Halbzeit, sonst Minute inkl. Nachspielzeit als "45+6'" - identisch zur Logik in
// MatchCard.vue/StartseiteWidget.vue.
const spieluhrText = computed(() => {
  if (!match.value) return ''
  if (match.value.spielUhrKurz === 'HT') return 'HT'
  if (typeof match.value.minute !== 'number' || match.value.minute < 0) return ''
  const extra = match.value.spielUhrExtra
  const extraSuffix = typeof extra === 'number' && extra > 0 ? `+${extra}` : ''
  return `${match.value.minute}${extraSuffix}'`
})

onMounted(async () => {
  try {
    match.value = await ladeEinzelnesMatch(props.apiBase, props.matchId)
    emit('match-loaded', match.value) // einmalig, komplettes Objekt aus mapMatch()
  } catch (e) {
    error.value = 'Spiel konnte nicht geladen werden.'
    console.warn('ladeEinzelnesMatch fehlgeschlagen:', e)
  } finally {
    loading.value = false
  }
  verbinden()
  wiederholeGepuffertes() // falls die geteilte WS-Verbindung schon vorher (durch ein anderes
  // Widget auf derselben Seite) offen war und Nachrichten verpasst wurden, hier nachholen
})

onUnmounted(() => {
  wsAufraeumen()
})
</script>